import { DEFAULT_MAX_PREVIEW_PIXELS, fitWithinPixels } from '@repo/design-renderer';
import { type Raster, rasterFrom } from '@repo/warp-engine';

export type ImageLoadFailureKind = 'expired' | 'network' | 'decode';

/** 이미지 로드 실패 — kind 로 재조회(expired)·재시도(network)·포기(decode)를 가른다. */
export class ImageLoadError extends Error {
  constructor(
    readonly kind: ImageLoadFailureKind,
    message: string,
  ) {
    super(message);
    this.name = 'ImageLoadError';
  }
}

type DecodedImage = CanvasImageSource & {
  readonly width: number;
  readonly height: number;
  readonly close?: () => void;
};

export interface LoadImageRasterOptions {
  readonly maxPixels?: number;
  readonly fetchImpl?: typeof fetch;
  readonly decode?: (blob: Blob) => Promise<DecodedImage>;
}

const defaultDecode = (blob: Blob): Promise<DecodedImage> => createImageBitmap(blob);

/**
 * 교차 출처 이미지(면 사진·마스크·음영·고객 이미지)를 픽셀로 읽는다. fetch(cors) → 디코딩 → 캔버스 → ImageData.
 * 서명 URL 만료(403)는 'expired' 로 구분해 호출자가 URL 을 다시 받게 한다. 상한을 넘는 픽셀은 비율을 유지해 줄인다.
 */
export const loadImageRaster = async (
  url: string,
  options: LoadImageRasterOptions = {},
): Promise<Raster> => {
  const fetchImpl = options.fetchImpl ?? fetch;
  const decode = options.decode ?? defaultDecode;
  let response: Response;
  try {
    response = await fetchImpl(url, { mode: 'cors', credentials: 'omit' });
  } catch (cause) {
    throw new ImageLoadError(
      'network',
      cause instanceof Error ? cause.message : '이미지를 받지 못했습니다.',
    );
  }
  if (response.status === 403) throw new ImageLoadError('expired', '이미지 주소가 만료됐습니다.');
  if (!response.ok) throw new ImageLoadError('network', `이미지 응답 ${response.status}`);
  let image: DecodedImage;
  try {
    image = await decode(await response.blob());
  } catch (cause) {
    throw new ImageLoadError(
      'decode',
      cause instanceof Error ? cause.message : '이미지를 해석하지 못했습니다.',
    );
  }
  try {
    const fit = fitWithinPixels(
      image.width,
      image.height,
      options.maxPixels ?? DEFAULT_MAX_PREVIEW_PIXELS,
    );
    const canvas = document.createElement('canvas');
    canvas.width = fit.width;
    canvas.height = fit.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new ImageLoadError('decode', '캔버스 2D 컨텍스트를 만들 수 없습니다.');
    ctx.drawImage(image, 0, 0, fit.width, fit.height);
    const { data } = ctx.getImageData(0, 0, fit.width, fit.height);
    return rasterFrom(fit.width, fit.height, new Uint8ClampedArray(data));
  } finally {
    image.close?.();
  }
};
